import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiPlus, FiLoader, FiInfo } from 'react-icons/fi';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import {
  ClienteSobreCard,
  ClienteSobreFiltros,
  ClienteSobrePaginacao,
} from '../components/clienteSobre';
import { clienteSobreAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.xl};
  border: none;
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: ${theme.fontSizes.base};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${theme.colors.primary[500]}30;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: ${theme.spacing[6]};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  background: ${theme.colors.background.secondary};
`;

const EmptyTitle = styled.h3`
  margin: ${theme.spacing[3]} 0 ${theme.spacing[2]};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  max-width: 480px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1.2s linear infinite;
  margin-bottom: ${theme.spacing[3]};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorState = styled(EmptyState)`
  border-color: ${theme.colors.error[400]};
`;

const ErrorTitle = styled(EmptyTitle)`
  color: ${theme.colors.error[500]};
`;

const ErrorText = styled(EmptyText)`
  color: ${theme.colors.error[400]};
`;

const RetryButton = styled.button`
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.primary[500]};
  background: transparent;
  color: ${theme.colors.primary[500]};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.text.primary};
  }
`;

const ClienteSobre = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [filtros, setFiltros] = useState({
    search: '',
    clienteId: '',
    page: 1,
    limit: 10,
  });
  const [clientesCarregados, setClientesCarregados] = useState(false);
  const [todasRevendasCadastradas, setTodasRevendasCadastradas] = useState(false);

  // Verificar se deve mostrar filtros (admin ou usuário com mais de 1 cliente)
  const deveMostrarFiltros = isAdmin() || clientes.length > 1;

  const carregarClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await clienteSobreAPI.listarClientes();
      if (response.data.success) {
        const clientesList = response.data.data || [];
        setClientes(clientesList);
        
        // Se não for admin e tiver apenas 1 cliente, filtrar automaticamente
        if (!isAdmin() && clientesList.length === 1) {
          const novoFiltro = {
            search: '',
            clienteId: String(clientesList[0].id),
            page: 1,
            limit: 10,
          };
          setFiltros(novoFiltro);
        }
        
        setClientesCarregados(true);
      } else {
        setClientes([]);
        setClientesCarregados(true);
      }
    } catch (err) {
      console.error('Erro ao carregar clientes disponíveis:', err);
      setClientes([]);
      setClientesCarregados(true);
    } finally {
      setLoadingClientes(false);
    }
  };

  const carregarRegistros = async (params = filtros) => {
    try {
      setLoading(true);
      setError(null);

      const response = await clienteSobreAPI.listar(params);
      if (response.data.success) {
        setRegistros(response.data.data || []);
        setPagination(response.data.pagination);
      } else {
        throw new Error(response.data.message || 'Erro ao carregar informações');
      }
    } catch (err) {
      console.error('Erro ao carregar cliente_sobre:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar informações');
    } finally {
      setLoading(false);
    }
  };

  // Verificar se todas as revendas acessíveis já foram cadastradas
  const verificarTodasRevendasCadastradas = async () => {
    if (clientes.length === 0) {
      setTodasRevendasCadastradas(false);
      return;
    }

    try {
      // Buscar todos os registros de cliente_sobre (sem paginação) para verificar quantos clientes únicos têm informações
      const response = await clienteSobreAPI.listar({
        search: '',
        clienteId: '',
        page: 1,
        limit: 1000, // Limite alto para pegar todos os registros
      });

      if (response.data.success) {
        const todosRegistros = response.data.data || [];
        // Obter IDs únicos de clientes que já têm informações cadastradas
        const clientesComInformacoes = new Set(
          todosRegistros.map((registro) => registro.cliente_id).filter(Boolean)
        );

        // Verificar se todos os clientes acessíveis já têm informações cadastradas
        const todosClientesTemInformacoes = clientes.every((cliente) =>
          clientesComInformacoes.has(cliente.id)
        );

        setTodasRevendasCadastradas(todosClientesTemInformacoes);
      } else {
        setTodasRevendasCadastradas(false);
      }
    } catch (err) {
      console.error('Erro ao verificar revendas cadastradas:', err);
      setTodasRevendasCadastradas(false);
    }
  };

  useEffect(() => {
    carregarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar registros quando os clientes forem carregados ou quando os filtros mudarem
  useEffect(() => {
    if (clientesCarregados) {
      carregarRegistros(filtros);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientesCarregados, filtros.clienteId, filtros.search, filtros.page, filtros.limit]);

  // Verificar se todas as revendas foram cadastradas quando os clientes forem carregados
  useEffect(() => {
    if (clientesCarregados && clientes.length > 0) {
      verificarTodasRevendasCadastradas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes.length, clientesCarregados]);

  const handleFiltrosChange = (novosFiltros) => {
    setFiltros((prev) => ({
      ...prev,
      ...novosFiltros,
      page: 1,
    }));
  };

  // Buscar com filtros (aceita parâmetros opcionais para usar filtros específicos)
  const handleBuscar = (param = null) => {
    const isEvent = param && typeof param === 'object' && typeof param.preventDefault === 'function';

    if (isEvent) {
      param.preventDefault();
    }

    const baseParams = isEvent
      ? { ...filtros }
      : (param || filtros);

    const params = {
      ...baseParams,
      page: baseParams?.page ?? 1,
    };

    setFiltros(params);
    carregarRegistros(params);
  };

  const handlePageChange = (page) => {
    const params = {
      ...filtros,
      page,
    };
    setFiltros(params);
    carregarRegistros(params);
  };

  const handleItemsPerPageChange = (limit) => {
    const params = {
      ...filtros,
      limit,
      page: 1,
    };
    setFiltros(params);
    carregarRegistros(params);
  };

  const handleAdicionar = () => {
    navigate('/cliente-sobre/novo');
  };

  const handleEditar = (registro) => {
    const identifier = registro?.uuid || registro?.id;
    if (!identifier) {
      return;
    }
    navigate(`/cliente-sobre/${identifier}/editar`);
  };


  const renderContent = () => {
    if (loading && registros.length === 0) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          Carregando informações da revenda...
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorTitle>Não foi possível carregar as informações</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={() => carregarRegistros(filtros)}>Tentar novamente</RetryButton>
        </ErrorState>
      );
    }

    if (registros.length === 0) {
      return (
        <EmptyState>
          <LoadingSpinner>
            <FiInfo />
          </LoadingSpinner>
          <EmptyTitle>Nenhuma informação cadastrada</EmptyTitle>
          <EmptyText>
            Cadastre as informações relevantes da revenda para facilitar o acesso aos dados pelos
            times da sua empresa.
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <>
        <Grid>
          {registros.map((registro) => (
            <ClienteSobreCard
              key={registro.uuid || registro.id}
              registro={registro}
              onEdit={handleEditar}
            />
          ))}
        </Grid>

        <ClienteSobrePaginacao
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={loading}
        />
      </>
    );
  };

  return (
    <Layout title="Informações da Revenda">
      <Container>
        <Header>
          <AddButton 
            onClick={handleAdicionar}
            disabled={todasRevendasCadastradas}
            title={todasRevendasCadastradas ? 'Todas as revendas acessíveis já possuem informações cadastradas' : 'Adicionar nova informação da revenda'}
          >
            <FiPlus />
            Nova Informação
          </AddButton>
        </Header>

        <Content>
          {deveMostrarFiltros && (
            <ClienteSobreFiltros
              filtros={filtros}
              onFiltrosChange={handleFiltrosChange}
              onBuscar={handleBuscar}
              loading={loading}
              clientes={clientes}
              loadingClientes={loadingClientes}
            />
          )}

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default ClienteSobre;


